import { useEffect, useRef, useState, type CSSProperties } from "react"
import { getBattleById, getLocationById, getUnitById } from "../game/content"
import {
  advanceToPlayerTurn,
  autoResolve,
  getActiveStack,
  getAvailableSpecial,
  getInitiativeOrder,
  getRemainingCount,
  getSpecialAction,
  getTargetableStacks,
  initializeBattle,
  performAction,
  previewDamage,
  resolveBattle,
  retreat,
  getPlayerBattleModifiers,
  type BattleActionKind,
  type BattleResolution,
  type BattleRuntimeState,
  type BattleStackState,
  type DamagePreview,
} from "../game/battleEngine"
import type { ArmyStack, PermanentState } from "../types"
import { ARMY_SCALE } from "../game/loopEngine"
import { UnitPortrait, UnitSpecialty, UnitTraits } from "../components/UnitTraits"
import { getFactionById } from "../game/content"
import { getFactionCrestStyle } from "../game/sprites"

interface BattleScreenProps {
  battleId: string
  playerStacks: ArmyStack[]
  permanent: PermanentState
  loopNumber: number
  day: number
  /** Pause held on the player's blow before the enemy answers it (see settings). */
  battlePaceMs: number
  onResolve: (resolution: BattleResolution) => void
}

/** Specials that need a target chosen; the rest resolve on the acting stack itself. */
const TARGETED_SPECIALS: BattleActionKind[] = [
  "volley",
  "charge",
  "banish",
  "sunder",
  "frenzy",
]

function healthShare(stack: BattleStackState): number {
  return stack.maxHealth === 0 ? 0 : stack.currentHealth / stack.maxHealth
}

/** One unit type on a side, and how many of it there are, for the roster panels. */
interface ForceTally {
  unitId: string
  name: string
  count: number
}

/**
 * Collapses a side's stacks to one line per unit type, in first-seen order — the
 * roster shown on the intro screen, and the baseline the aftermath measures its
 * casualties against.
 */
function tallyForces(stacks: BattleStackState[]): ForceTally[] {
  const order: string[] = []
  const counts = new Map<string, number>()
  for (const stack of stacks) {
    if (!counts.has(stack.unitId)) order.push(stack.unitId)
    counts.set(stack.unitId, (counts.get(stack.unitId) ?? 0) + getRemainingCount(stack))
  }
  return order.map((unitId) => ({
    unitId,
    name: getUnitById(unitId)?.name ?? unitId,
    count: counts.get(unitId) ?? 0,
  }))
}

interface CasualtyLine {
  unitId: string
  name: string
  initial: number
  remaining: number
  lost: number
}

/** Per-unit-type casualties for a side: what it started with, kept, and lost. */
function casualties(initial: ForceTally[], survivors: BattleStackState[]): {
  lines: CasualtyLine[]
  totalLost: number
} {
  const remaining = new Map<string, number>()
  for (const tally of tallyForces(survivors)) remaining.set(tally.unitId, tally.count)
  let totalLost = 0
  const lines = initial.map((entry) => {
    const kept = remaining.get(entry.unitId) ?? 0
    const lost = Math.max(0, entry.count - kept)
    totalLost += lost
    return {
      unitId: entry.unitId,
      name: entry.name,
      initial: entry.count,
      remaining: kept,
      lost,
    }
  })
  return { lines, totalLost }
}

/**
 * The banner the enemy fights under, read off its own troops: the faction most
 * of the enemy stacks belong to. A field army has one standard even when it
 * mixes a stack of another power's auxiliaries, so the modal faction — not the
 * first stack — is what the header should fly. Null if the enemy has no faction
 * with a crest (a nameless muster, or a faction past the sheet).
 */
function enemyBanner(stacks: BattleStackState[]): { name: string; crest: CSSProperties } | null {
  const tally = new Map<string, number>()
  for (const stack of stacks) {
    const factionId = getUnitById(stack.unitId)?.factionId
    if (factionId) tally.set(factionId, (tally.get(factionId) ?? 0) + 1)
  }
  let bestId: string | null = null
  let bestCount = 0
  for (const [factionId, count] of tally) {
    if (count > bestCount) {
      bestId = factionId
      bestCount = count
    }
  }
  if (!bestId) return null
  const crest = getFactionCrestStyle(bestId)
  if (!crest) return null
  return { name: getFactionById(bestId)?.name ?? bestId, crest }
}

function StackCard({
  stack,
  active,
  selectable,
  onClick,
  preview,
}: {
  stack: BattleStackState
  active?: boolean
  selectable?: boolean
  onClick?: () => void
  preview?: DamagePreview | null
}) {
  const unit = getUnitById(stack.unitId)
  const count = getRemainingCount(stack)

  // A blow lands, or wounds knit closed: whenever this stack's health pool moves
  // between renders, shake the card and float the number over it — a red "-N" for
  // a hit, a green "+N" for a heal. The health bar itself drains via a CSS
  // transition, so the pool visibly falls rather than jumping.
  const [flash, setFlash] = useState<{ delta: number; id: number } | null>(null)
  const prevHealth = useRef(stack.currentHealth)
  useEffect(() => {
    const delta = stack.currentHealth - prevHealth.current
    prevHealth.current = stack.currentHealth
    if (delta === 0) return
    const id = Date.now() + Math.random()
    setFlash({ delta, id })
    const timer = window.setTimeout(() => {
      setFlash((current) => (current && current.id === id ? null : current))
    }, 850)
    return () => window.clearTimeout(timer)
  }, [stack.currentHealth])

  const classNames = [
    "stack-card",
    active ? "active" : "",
    stack.defending ? "defending" : "",
    stack.guardDropped ? "exposed" : "",
    stack.concealed ? "concealed" : "",
    flash ? (flash.delta < 0 ? "struck" : "mended") : "",
  ]
    .filter(Boolean)
    .join(" ")

  const hasStatus =
    stack.concealed || stack.defending || stack.guardDropped || stack.skipRounds > 0

  const body = (
    <>
      {flash && (
        <span
          key={flash.id}
          className={`stack-float ${flash.delta < 0 ? "damage" : "heal"}`}
          aria-hidden="true"
        >
          {flash.delta < 0 ? flash.delta : `+${flash.delta}`}
        </span>
      )}
      {/* Always-visible summary: who they are, how many, and how much fight is
          left in them. Everything else waits behind a hover. */}
      <span className="stack-card-head">
        {unit && <UnitPortrait unit={unit} size="sm" />}
        <span className="stack-headline">
          <span className="stack-name">{unit?.name ?? stack.unitId}</span>
          <span className="stack-figures">
            <span className="stack-count">×{count}</span>
            <span className="stack-hp">
              {stack.currentHealth}/{stack.maxHealth} hp
            </span>
          </span>
        </span>
      </span>
      <span className="stack-health-bar" aria-hidden="true">
        <span style={{ width: `${Math.round(healthShare(stack) * 100)}%` }} />
      </span>

      {/* The composition detail — counters, resistances, specialty, live status,
          and the expected-damage preview when this is a target you can strike —
          surfaces on hover or keyboard focus rather than crowding the card. */}
      <span className="stack-details" role="tooltip">
        {preview && (
          <span className="stack-preview">
            {preview.min === preview.max
              ? `${preview.min} damage`
              : `${preview.min}–${preview.max} damage`}
            {preview.counterMultiplier > 1 && ` · counter ×${preview.counterMultiplier}`}
            {preview.resisted && " · resisted"}
            {preview.blind && " · striking blind"}
          </span>
        )}
        {unit && <UnitTraits unit={unit} />}
        {unit && <UnitSpecialty unit={unit} />}
        {hasStatus && (
          <span className="stack-status">
            {stack.concealed && <em>hidden — cannot be targeted</em>}
            {stack.defending && <em>defending</em>}
            {stack.guardDropped && <em>guard down</em>}
            {stack.skipRounds > 0 && <em>reloading</em>}
          </span>
        )}
      </span>
    </>
  )

  return (
    <li className="stack-cell">
      {selectable ? (
        <button type="button" className={classNames} onClick={onClick}>
          {body}
        </button>
      ) : (
        <div className={classNames}>{body}</div>
      )}
    </li>
  )
}

/** The intro roster for one side: the line of battle, one row per unit type. */
function RosterPanel({
  title,
  forces,
  enemy,
  crest,
}: {
  title: string
  forces: ForceTally[]
  enemy?: boolean
  crest?: { name: string; crest: CSSProperties } | null
}) {
  const total = forces.reduce((sum, entry) => sum + entry.count, 0)
  return (
    <section className={`roster-panel${enemy ? " roster-enemy" : ""}`}>
      <h2 className="roster-head">
        {crest && (
          <span
            className="battle-force-crest"
            style={crest.crest}
            role="img"
            aria-label={`${crest.name} crest`}
          />
        )}
        {title}
      </h2>
      <ul className="roster-list">
        {forces.length === 0 && <li className="roster-empty">No one stands here.</li>}
        {forces.map((entry) => {
          const unit = getUnitById(entry.unitId)
          return (
            <li key={entry.unitId} className="roster-row">
              <span className="roster-row-head">
                {unit && <UnitPortrait unit={unit} size="sm" />}
                <span className="roster-name">{entry.name}</span>
                <span className="roster-count">×{entry.count}</span>
              </span>
              {unit && <UnitTraits unit={unit} />}
            </li>
          )
        })}
      </ul>
      {total > 0 && <p className="roster-total">{total} in the line</p>}
    </section>
  )
}

/** The aftermath roster for one side: what each unit type started, kept and lost. */
function CasualtyPanel({
  title,
  result,
  enemy,
}: {
  title: string
  result: { lines: CasualtyLine[]; totalLost: number }
  enemy?: boolean
}) {
  return (
    <section className={`roster-panel casualty-panel${enemy ? " roster-enemy" : ""}`}>
      <h2 className="roster-head">{title}</h2>
      <ul className="roster-list">
        {result.lines.length === 0 && <li className="roster-empty">—</li>}
        {result.lines.map((line) => {
          const unit = getUnitById(line.unitId)
          return (
            <li key={line.unitId} className="roster-row casualty-row">
              <span className="roster-row-head">
                {unit && <UnitPortrait unit={unit} size="sm" />}
                <span className="roster-name">{line.name}</span>
                <span className={`casualty-figure${line.lost > 0 ? " has-loss" : ""}`}>
                  {line.lost > 0 ? `−${line.lost}` : "held"}
                </span>
              </span>
              <span className="casualty-detail">
                {line.remaining} of {line.initial} still standing
              </span>
            </li>
          )
        })}
      </ul>
      <p className="roster-total">
        {result.totalLost > 0 ? `${result.totalLost} lost` : "No losses"}
      </p>
    </section>
  )
}

export function BattleScreen({
  battleId,
  playerStacks,
  permanent,
  loopNumber,
  day,
  battlePaceMs,
  onResolve,
}: BattleScreenProps) {
  const battle = getBattleById(battleId)

  const [battleState, setBattleState] = useState<BattleRuntimeState | null>(() => {
    if (!battle) return null
    // Held at its pristine, full-strength setup for the intro screen — the enemy
    // does not get to move until the player takes the field (see beginBattle).
    return initializeBattle(
      battle,
      playerStacks,
      loopNumber,
      day,
      getPlayerBattleModifiers(permanent),
    )
  })
  // The battle runs in three beats: the muster (intro), the fighting itself, and
  // the aftermath. The intro and aftermath are the two illustrated bookends.
  const [phase, setPhase] = useState<"intro" | "fighting" | "aftermath">("intro")
  const [pendingAction, setPendingAction] = useState<BattleActionKind | null>(null)
  // Which irreversible battle action is waiting on a second click, if any.
  const [confirming, setConfirming] = useState<"auto" | "retreat" | null>(null)
  // True while the field is holding on the player's just-struck blow, before the
  // enemy's turns resolve — the "battle speed" pause. Player input is withheld
  // for its duration so a click can't land on a stack mid-resolution.
  const [resolving, setResolving] = useState(false)
  const paceTimer = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (paceTimer.current !== null) window.clearTimeout(paceTimer.current)
    },
    [],
  )

  // The forces each side mustered with, snapped once from the pristine setup so
  // the aftermath can count the fallen against the line that marched in.
  const initialForces = useRef<{ friendly: ForceTally[]; enemy: ForceTally[] } | null>(null)
  if (battleState && initialForces.current === null) {
    initialForces.current = {
      friendly: tallyForces(battleState.friendlyStacks),
      enemy: tallyForces(battleState.enemyStacks),
    }
  }

  // Once the fighting is decided, hold on the settled field a beat — long enough
  // for the last blow to land visibly — then draw the aftermath over it.
  const outcomeNow = battleState?.outcome ?? "ongoing"
  useEffect(() => {
    if (phase !== "fighting" || outcomeNow === "ongoing") return
    const timer = window.setTimeout(() => setPhase("aftermath"), 950)
    return () => window.clearTimeout(timer)
  }, [phase, outcomeNow])

  if (!battle || !battleState) {
    return (
      <div className="screen battle-screen">
        <p>Unknown battle.</p>
      </div>
    )
  }

  const state = battleState
  const outcome = state.outcome
  const active = getActiveStack(state)
  const activeUnit = active ? getUnitById(active.unitId) : undefined
  const special = active ? getAvailableSpecial(active) : null
  const specialDefinition = active ? getSpecialAction(active) : null
  const targetable = active ? getTargetableStacks(state, active) : []
  // Judged from the starting line, not the current one — a player who fought
  // and lost everyone has not "stood alone", they were beaten.
  const startedWithNoAllies = state.initialFriendlyKeys.length === 0

  function commit(next: BattleRuntimeState) {
    setPendingAction(null)
    setConfirming(null)
    // A finished fight, or an instant pace, settles with no pause.
    if (next.outcome !== "ongoing" || battlePaceMs <= 0) {
      setBattleState(next.outcome === "ongoing" ? advanceToPlayerTurn(next, battle!) : next)
      return
    }
    // Show the player's blow land, hold briefly, then let the enemy answer.
    setBattleState(next)
    setResolving(true)
    if (paceTimer.current !== null) window.clearTimeout(paceTimer.current)
    paceTimer.current = window.setTimeout(() => {
      setBattleState(advanceToPlayerTurn(next, battle!))
      setResolving(false)
      paceTimer.current = null
    }, battlePaceMs)
  }

  function handleTargetChosen(targetKey: string) {
    if (!active) return
    commit(
      performAction(state, battle!, active.stackKey, pendingAction ?? "attack", targetKey),
    )
  }

  function handleSelfAction(kind: BattleActionKind) {
    if (!active) return
    commit(performAction(state, battle!, active.stackKey, kind))
  }

  function handleAutoResolve() {
    commit(autoResolve(state, battle!))
  }

  function handleRetreat() {
    setBattleState(retreat(state))
    setPendingAction(null)
    setConfirming(null)
  }

  function handleContinue() {
    onResolve(resolveBattle(battle!, state))
  }

  // Taking the field: the enemy's faster stacks finally get to move, and the
  // fighting screen takes over from the muster.
  function beginBattle() {
    setBattleState(advanceToPlayerTurn(state, battle!))
    setPhase("fighting")
  }

  const resolution =
    outcome === "ongoing" ? null : resolveBattle(battle, state)

  const locationName =
    getLocationById(battle.locationId)?.name ?? battle.name

  // --- The muster: who is drawn up, and where, before a blow is struck. -------
  if (phase === "intro") {
    return (
      <div
        className="screen battle-screen battle-bookend battle-intro"
        style={{ "--battle-bg": 'url("/img/bg_battle_start.png")' } as CSSProperties}
      >
        <div className="battle-scene">
          <p className="battle-scene-place">{locationName}</p>
          <h1>{battle.name}</h1>
          {battle.briefing && <p className="battle-briefing">{battle.briefing}</p>}
        </div>

        <div className="battle-muster">
          <RosterPanel title="Your Host" forces={initialForces.current?.friendly ?? []} />
          <RosterPanel
            title="The Enemy"
            forces={initialForces.current?.enemy ?? []}
            enemy
            crest={enemyBanner(state.enemyStacks)}
          />
        </div>

        <div className="battle-bookend-actions">
          <button type="button" className="battle-begin" onClick={beginBattle}>
            Take the Field
          </button>
        </div>
      </div>
    )
  }

  // --- The aftermath: the field settled, and the price of it counted. ---------
  if (phase === "aftermath" && resolution) {
    const won = outcome === "victory"
    const bg = won ? "/img/bg_battle_won.png" : "/img/bg_battle_lost.png"
    const initial = initialForces.current
    const friendlyLosses = initial
      ? casualties(initial.friendly, state.friendlyStacks)
      : { lines: [], totalLost: 0 }
    const enemyLosses = initial
      ? casualties(initial.enemy, state.enemyStacks)
      : { lines: [], totalLost: 0 }
    const lethalDefeat = outcome === "defeat" && !battle.nonLethal
    const title =
      outcome === "victory"
        ? "The Field Is Won"
        : outcome === "retreat"
          ? "You Give Up the Field"
          : lethalDefeat
            ? "The Line Is Broken"
            : "You Are Bested"
    const flavour =
      outcome === "victory"
        ? `The enemy is broken, after ${state.round} round${state.round === 1 ? "" : "s"}.`
        : outcome === "retreat"
          ? "You pull your people back off the field. The ground is theirs."
          : startedWithNoAllies
            ? "You had no one to fight beside you. The line does not hold."
            : "Your forces are overwhelmed and the field is lost."
    const buttonLabel =
      outcome === "victory"
        ? "Claim the Field"
        : outcome === "retreat"
          ? "Fall Back"
          : lethalDefeat
            ? "Meet Your End"
            : "Continue"

    return (
      <div
        className={`screen battle-screen battle-bookend battle-aftermath ${
          won ? "is-won" : "is-lost"
        }`}
        style={{ "--battle-bg": `url("${bg}")` } as CSSProperties}
      >
        <div className="battle-scene">
          <p className="battle-scene-place">{locationName}</p>
          <h1>{title}</h1>
          <p className="battle-briefing">{flavour}</p>
        </div>

        {resolution.objectives.length > 0 && (
          <div className="battle-objectives battle-aftermath-objectives">
            <h2>Objectives</h2>
            <ul>
              {resolution.objectives.map((result) => (
                <li
                  key={result.objective.id}
                  className={result.achieved ? "objective-met" : "objective-failed"}
                >
                  {result.objective.description}
                  {result.achieved ? " — achieved" : " — failed"}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="battle-muster">
          <CasualtyPanel title="Your Losses" result={friendlyLosses} />
          <CasualtyPanel title="Enemy Losses" result={enemyLosses} enemy />
        </div>

        {(resolution.lootMoney > 0 || resolution.commandPointReward > 0) && (
          <div className="battle-spoils">
            {resolution.lootMoney > 0 && (
              <p>You recover {resolution.lootMoney} coin from the field.</p>
            )}
            {resolution.commandPointReward > 0 && (
              <p>
                The field is yours to command: +
                {resolution.commandPointReward * ARMY_SCALE} Command Points for the
                rest of the loop.
              </p>
            )}
          </div>
        )}

        <div className="battle-bookend-actions">
          <button type="button" className="battle-begin" onClick={handleContinue}>
            {buttonLabel}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen battle-screen">
      <h1>{battle.name}</h1>
      {battle.briefing && state.round <= 1 && outcome === "ongoing" && (
        <p className="battle-briefing">{battle.briefing}</p>
      )}

      {battle.objectives && battle.objectives.length > 0 && (
        <div className="battle-objectives">
          <h2>Objectives</h2>
          <ul>
            {battle.objectives.map((objective) => {
              const result = resolution?.objectives.find(
                (r) => r.objective.id === objective.id,
              )
              return (
                <li
                  key={objective.id}
                  className={
                    result ? (result.achieved ? "objective-met" : "objective-failed") : ""
                  }
                >
                  {objective.description}
                  {result && (result.achieved ? " — achieved" : " — failed")}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {outcome === "ongoing" && (
        <div className="battle-turn-bar">
          <span className="battle-round">Round {state.round}</span>
          <ol className="initiative-track">
            {getInitiativeOrder(state).map((stack) => (
              <li
                key={stack.stackKey}
                className={[
                  stack.side,
                  stack.stackKey === active?.stackKey ? "acting" : "",
                  state.actedKeys.includes(stack.stackKey) ? "spent" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {getUnitById(stack.unitId)?.name ?? stack.unitId}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* The battlefield proper: your line drawn up on the left, the enemy line
          facing it on the right. No maneuver — the stacks are placed, not moved,
          and each side flows into a second column only once it outgrows one. */}
      <div className="battle-field">
        <section className="battle-side battle-side-friendly">
          <h2>Your Forces</h2>
          <ul
            className={`stack-grid${state.friendlyStacks.length > 4 ? " two-col" : ""}`}
          >
            {state.friendlyStacks.length === 0 && (
              <li className="stack-empty">No one stands with you.</li>
            )}
            {state.friendlyStacks.map((stack) => (
              <StackCard
                key={stack.stackKey}
                stack={stack}
                active={stack.stackKey === active?.stackKey}
              />
            ))}
          </ul>
        </section>

        <section className="battle-side battle-side-enemy">
          <h2 className="battle-force-header">
            {(() => {
              const banner = enemyBanner(state.enemyStacks)
              return (
                banner && (
                  <span
                    className="battle-force-crest"
                    style={banner.crest}
                    role="img"
                    aria-label={`${banner.name} crest`}
                  />
                )
              )
            })()}
            Enemy Forces
          </h2>
          <ul className={`stack-grid${state.enemyStacks.length > 4 ? " two-col" : ""}`}>
            {state.enemyStacks.map((stack) => {
              const canTarget =
                outcome === "ongoing" &&
                !resolving &&
                active !== null &&
                targetable.some((t) => t.stackKey === stack.stackKey)
              return (
                <StackCard
                  key={stack.stackKey}
                  stack={stack}
                  selectable={canTarget}
                  onClick={canTarget ? () => handleTargetChosen(stack.stackKey) : undefined}
                  preview={
                    canTarget && active
                      ? previewDamage(
                          state,
                          active.stackKey,
                          stack.stackKey,
                          pendingAction ?? "attack",
                        )
                      : null
                  }
                />
              )
            })}
          </ul>
        </section>
      </div>

      {/* Actions and the running log sit below the two lines, where they don't
          fight the field for width. */}
      <div className="battle-controls">
          {outcome === "ongoing" && resolving && (
            <div className="battle-actions battle-actions-panel">
              <p className="battle-note battle-resolving">The blow lands. The enemy answers…</p>
            </div>
          )}
          {outcome === "ongoing" && !resolving && active && (
            <div className="battle-actions battle-actions-panel">
              <p className="battle-note">
                <strong>{activeUnit?.name ?? active.unitId}</strong> is ready.{" "}
                {pendingAction && pendingAction !== "attack"
                  ? `Choose a target for ${specialDefinition?.name ?? pendingAction}.`
                  : "Choose an enemy stack to attack, or take another action."}
              </p>
              <div className="action-buttons">
                <button
                  type="button"
                  className={
                    pendingAction === null || pendingAction === "attack" ? "selected" : ""
                  }
                  onClick={() => setPendingAction("attack")}
                >
                  Attack
                </button>
                <button type="button" onClick={() => handleSelfAction("defend")}>
                  Defend
                </button>
                {special && specialDefinition && (
                  <button
                    type="button"
                    className={pendingAction === special ? "selected" : ""}
                    title={specialDefinition.description}
                    onClick={() =>
                      TARGETED_SPECIALS.includes(special)
                        ? setPendingAction(special)
                        : handleSelfAction(special)
                    }
                  >
                    {specialDefinition.name}
                  </button>
                )}
                {/* Arming a special used to be a one-way door: the only way
                    out of it was to spend it on something. */}
                {pendingAction !== null && pendingAction !== "attack" && (
                  <button type="button" onClick={() => setPendingAction("attack")}>
                    Cancel {specialDefinition?.name ?? "Special"}
                  </button>
                )}
                {/* Both of these hand the rest of the fight away in one click,
                    and either can end a loop the player has spent an hour on.
                    They sit beside ordinary turn actions, so they ask twice. */}
                {confirming === "auto" ? (
                  <>
                    <button type="button" className="danger" onClick={handleAutoResolve}>
                      Confirm: fight it out without me
                    </button>
                    <button type="button" onClick={() => setConfirming(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => setConfirming("auto")}>
                    Resolve Automatically
                  </button>
                )}
                {battle.allowRetreat &&
                  (confirming === "retreat" ? (
                    <>
                      <button type="button" className="danger" onClick={handleRetreat}>
                        Confirm: withdraw and give up the field
                      </button>
                      <button type="button" onClick={() => setConfirming(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="retreat-button"
                      onClick={() => setConfirming("retreat")}
                    >
                      Withdraw (lose a quarter of your army)
                    </button>
                  ))}
              </div>
              {special && specialDefinition && (
                <p className="special-description">
                  <strong>{specialDefinition.name}:</strong> {specialDefinition.description}
                </p>
              )}
            </div>
          )}

          {/* The fight is decided but the aftermath has not yet drawn over the
              field — hold on the settled line for the beat the effect above
              waits out, so the last blow reads before the summary appears. */}
          {outcome !== "ongoing" && (
            <div className="battle-actions battle-actions-panel">
              <p className="battle-note battle-resolving">
                {outcome === "victory"
                  ? "The enemy breaks. The dust settles…"
                  : outcome === "retreat"
                    ? "You break off. The dust settles…"
                    : "The line is broken. The dust settles…"}
              </p>
            </div>
          )}

          {state.log.length > 0 && (
            <div className="battle-log-wrap">
              <h2>Battle Log</h2>
              {/* Newest first: the line that matters is the one describing the
                  blow just struck, and appending to the bottom of an
                  ever-growing list buried it further away every round. */}
              <div className="battle-log-frame">
                <ul className="battle-log">
                  {state.log
                    .map((entry, index) => ({ entry, index }))
                    .reverse()
                    .map(({ entry, index }) => (
                      <li key={index}>{entry}</li>
                    ))}
                </ul>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}
